import ArticleLayout, { H2, H3, Lead, Note, BackHome } from "@/components/ArticleLayout";
import { IMAGES } from "@/lib/config";

export default function PeinturesDecorativesBatiAncien() {
    return (
        <ArticleLayout
            slug="peintures-decoratives-bati-ancien"
            title={<>Peintures & finitions décoratives <span className="italic text-brand">compatibles bâti ancien</span></>}
            metaTitle="Peintures & finitions décoratives compatibles bâti ancien"
            subtitle="Badigeons de chaux, laits de chaux, peintures minérales, stucs et patines : redonner à un mur ancien une finition sensible et respirante."
            breadcrumbLabel="Peintures décoratives"
            metaDescription="Peintures et finitions décoratives adaptées au bâti ancien : chaux (badigeon, lait, patine), silicate, caséine, terre. Techniques artisanales par un artisan de l'Eure-et-Loir."
            coverImage={IMAGES.painting}
            coverAlt="Intérieur décoré avec peintures compatibles bâti ancien — mur travaillé et décoration artisanale"
            updated="2026-01-15"
        >
            <Lead>
                Une peinture sur bâti ancien n'est jamais anodine : elle est ce qui touche le mur en dernier
                et qui, à elle seule, peut annuler ou parfaire tout le travail d'enduit qui l'a précédée.
            </Lead>

            <H2>Ce qu'une bonne peinture pour bâti ancien doit faire</H2>
            <p>
                Sur un mur qui respire, on ne peint pas comme sur du placo. Une finition adaptée doit :
            </p>
            <ul className="list-disc pl-6 space-y-2">
                <li><strong>Laisser passer la vapeur d'eau</strong> (perméabilité, µ &lt; 20)</li>
                <li><strong>Adhérer sans former de film étanche</strong> (pas d'acrylique ni de vinylique)</li>
                <li><strong>Résister à l'alcalinité</strong> de la chaux fraîche (si posée sur un enduit récent)</li>
                <li><strong>Vieillir avec le mur</strong> — patine douce, pas de cloques ni d'écaillage</li>
            </ul>

            <H2>Les 5 finitions décoratives artisanales de référence</H2>

            <H3>1. Le badigeon de chaux</H3>
            <p>
                Chaux aérienne + eau + pigments naturels. Deux à trois couches croisées à la brosse.
                Le badigeon <strong>fait corps avec le support</strong> par carbonatation, ne s'écaille jamais et
                développe une <em>vibration</em> de teinte incomparable. Palette de nuances : blancs cassés, beiges,
                ocres, terres.
            </p>
            <p>
                Idéal en intérieur, en extérieur également si abrité. Peut se patiner à l'éponge ou au chiffon
                pour l'effet nuageux typique des vieilles maisons.
            </p>

            <H3>2. Le lait de chaux</H3>
            <p>
                Cousin plus dilué du badigeon (plus d'eau). Il donne une couleur plus légère, plus laiteuse,
                très luminescente. Souvent utilisé en <strong>eau forte</strong> pour raviver un mur ancien
                sans le masquer.
            </p>

            <H3>3. La peinture minérale au silicate</H3>
            <p>
                À base de silicate de potassium, elle se lie chimiquement au support minéral (chaux, plâtre,
                béton). Grande perméabilité à la vapeur d'eau, très bonne tenue extérieure, teintes stables
                dans le temps.
            </p>
            <p>
                Excellente alternative si le badigeon paraît trop fragile pour l'usage attendu (couloir de
                passage, cage d'escalier, façade exposée).
            </p>

            <H3>4. La peinture à la caséine</H3>
            <p>
                Peinture ancestrale à base de lait — caséine, chaux et pigments. Fini profondément mat, très
                velouté au toucher, entièrement écologique. Utilisée sur bois ou sur enduit.
            </p>

            <H3>5. Le stuc & le tadelakt</H3>
            <p>
                Pour les projets décoratifs plus ambitieux : le <strong>stuc à la chaux</strong> (chaux fine
                serrée à la lisse) donne un fini poli, presque marbré. Le <strong>tadelakt</strong> (chaux de
                Marrakech serrée au galet et savonnée) est étanche et lustré — idéal en salle de bain sur
                enduits chaux.
            </p>

            <H2>Ce qu'il ne faut jamais faire sur un mur ancien</H2>
            <Note>
                <ul className="list-disc pl-6 space-y-1">
                    <li>Poser une peinture acrylique, vinylique ou glycéro sur un enduit à la chaux</li>
                    <li>Peindre un mur humide (attendre 3 à 12 mois selon l'enduit)</li>
                    <li>Utiliser un fixateur synthétique pour « bloquer » un support poudreux</li>
                    <li>Rénover à l'identique avec un revêtement « lessivable » standard</li>
                </ul>
            </Note>

            <H2>Combien de temps ça tient ?</H2>
            <p>
                Un badigeon de chaux bien appliqué tient <strong>10 à 20 ans</strong> en intérieur, 6 à 12 ans
                en extérieur exposé. Il ne s'écaille jamais : il s'estompe. Un « rafraîchissement » se fait
                d'une simple couche neuve, sans décapage.
            </p>
            <p>
                Une peinture minérale silicate : <strong>15 à 25 ans</strong> en extérieur bien exposé.
            </p>

            <H2>Coût indicatif</H2>
            <ul className="list-disc pl-6 space-y-2">
                <li>Badigeon 2 couches, préparation incluse — <strong>22 à 35 €/m²</strong></li>
                <li>Peinture minérale silicate — <strong>30 à 50 €/m²</strong></li>
                <li>Stuc chaux fin — <strong>90 à 150 €/m²</strong></li>
                <li>Tadelakt salle de bain — <strong>140 à 220 €/m²</strong></li>
            </ul>

            <H2>Notre approche à l'Atelier</H2>
            <p>
                Chaque projet de peinture ou finition décorative commence par un test in situ :
                <strong> échantillons de teintes</strong> à sec et humide, essai sur une zone témoin, choix
                arrêté après 48 h de séchage. C'est la seule manière fiable de valider une nuance sur un mur
                ancien — la teinte pot ≠ la teinte mur sec.
            </p>

            <Note tone="sand">
                🎨 Envie d'une couleur juste, appliquée artisanalement ?{" "}
                <a href="tel:+33664208134" className="text-brand hover:underline font-medium">06 64 20 81 34</a>.
            </Note>

            <BackHome />
        </ArticleLayout>
    );
}
